import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Button, Row, Col, Checkbox, message, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, resetLoginFlag } from '../../store/actions';
import { useNavigate } from 'react-router-dom';
import lottie from 'lottie-web';
import animationData from '../../assets/lottie/loginAnimation.json';
import { EyeInvisibleOutlined, EyeTwoTone, UserOutlined, LockOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';

function ConfidooLogin() {
    const lottieRef = useRef(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { error, errorMsg, twoFactorRequired } = useSelector((state) => state.Login);
    const [is2FAModalVisible, setIs2FAModalVisible] = useState(false);
    const [totpCode, setTotpCode] = useState('');
    const [loginCredentials, setLoginCredentials] = useState(null);

    // Initialize Lottie animation with cleanup
    useEffect(() => {
        const anim = lottie.loadAnimation({
            container: lottieRef.current,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: animationData,
        });
        return () => anim.destroy(); // Cleanup on unmount
    }, []);

    // Debounced effect for state updates to reduce re-renders
    useEffect(() => {
        const timer = setTimeout(() => {
            if (twoFactorRequired) {
                setIs2FAModalVisible(true);
            } else if (error) {
                message.error(errorMsg || 'Login failed. Please check your credentials and try again');
            }
            dispatch(resetLoginFlag());
            setLoading(false);
        }, 100);
        return () => clearTimeout(timer);
    }, [twoFactorRequired, error, errorMsg, dispatch]);

    // Navigate if user is already logged in
    useEffect(() => {
        const user = localStorage.getItem('user');
        if (user) navigate('/dashboard');
    }, [navigate]);

    // Debounced form submission to prevent rapid retries
    const handleSubmit = useCallback(
        debounce((values) => {
            setLoading(true);
            console.log('Login form submitted with values:', values);
            setLoginCredentials(values);
            dispatch(loginUser({ user: { ...values, totpCode },  navigate }));
        }, 1000),
        [dispatch, navigate]
    );

    const handle2FASubmit = () => {
        if (!totpCode || !/^\d{6}$/.test(totpCode)) {
            message.error('Please enter a valid 6-digit TOTP code');
            return;
        }
        setLoading(true);
        dispatch(loginUser({ user: { ...loginCredentials, totpCode }, navigate: navigate }));
        setTotpCode('');
    };

    const handleModalCancel = () => {
        setIs2FAModalVisible(false);
        setTotpCode('');
        setLoading(false);
    };

    const customStyles = `
    .confidoo-login-container {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
      position: relative;
      overflow: hidden;
    }
    
    .confidoo-login-container::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(0, 72, 142, 0.03) 0%, transparent 70%);
      animation: float 20s ease-in-out infinite;
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-20px) rotate(180deg); }
    }
    
    .confidoo-form-container {
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      box-shadow: 0 20px 40px rgba(0, 72, 142, 0.1), 0 8px 16px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.2);
      position: relative;
      overflow: hidden;
    }
    
    .confidoo-form-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #00488e 0%, #ff6b35 50%, #0ea5e9 100%);
    }
    
    .confidoo-welcome-text {
      background: linear-gradient(135deg, #00488e, #0ea5e9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-weight: 700;
      margin-bottom: 8px;
    }
    
    .confidoo-subtitle {
      color: #64748b;
      font-weight: 500;
      margin-bottom: 32px;
    }
    
    .confidoo-input {
      border-radius: 12px !important;
      border: 2px solid #e2e8f0 !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      background: rgba(248, 250, 252, 0.8) !important;
    }
    
    .confidoo-input:hover {
      border-color: #00488e !important;
      box-shadow: 0 4px 12px rgba(0, 72, 142, 0.1) !important;
    }
    
    .confidoo-input:focus, .confidoo-input.ant-input-focused {
      border-color: #00488e !important;
      box-shadow: 0 0 0 4px rgba(0, 72, 142, 0.1) !important;
      background: white !important;
    }
    
    .confidoo-button {
      height: 48px !important;
      border-radius: 12px !important;
      font-weight: 600 !important;
      font-size: 16px !important;
      background: linear-gradient(135deg, #00488e 0%, #0ea5e9 100%) !important;
      border: none !important;
      box-shadow: 0 8px 24px rgba(0, 72, 142, 0.3) !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      position: relative !important;
      overflow: hidden !important;
    }
    
    .confidoo-button::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
    }
    
    .confidoo-button:hover::before {
      left: 100%;
    }
    
    .confidoo-button:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 12px 32px rgba(0, 72, 142, 0.4) !important;
    }
    
    .confidoo-button:active {
      transform: translateY(0px) !important;
    }
    
    .confidoo-forgot-link {
      color: #00488e !important;
      font-weight: 500 !important;
      position: relative !important;
      transition: all 0.3s ease !important;
    }
    
    .confidoo-forgot-link::after {
      content: '';
      position: absolute;
      width: 0;
      height: 2px;
      bottom: -2px;
      left: 0;
      background: linear-gradient(90deg, #00488e, #ff6b35);
      transition: width 0.3s ease;
    }
    
    .confidoo-forgot-link:hover::after {
      width: 100%;
    }
    
    .confidoo-checkbox {
      color: #64748b !important;
    }
    
    .confidoo-logo-section {
      position: relative;
      overflow: hidden;
    }
    
    .confidoo-logo-section::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at 20% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(14, 165, 233, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 60%, rgba(0, 72, 142, 0.1) 0%, transparent 50%);
    }
    
    .confidoo-brand-container {
      position: relative;
      z-index: 2;
      text-align: center;
      padding: 40px;
    }
    
    .confidoo-brand-title {
      color: white;
      font-size: 2.5rem;
      font-weight: 800;
      margin: 24px 0 16px 0;
      letter-spacing: 2px;
      text-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }
    
    .confidoo-brand-tagline {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 1px;
    }
    
    .confidoo-brand-dot {
      width: 6px;
      height: 6px;
      background: linear-gradient(45deg, #ff6b35, #fbbf24);
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(255, 107, 53, 0.3);
    }
    
    .confidoo-lottie-container {
      position: relative;
      z-index: 1;
      filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.2));
    }
    
    @media (max-width: 768px) {
      .confidoo-form-container {
        margin: 20px;
        padding: 32px 24px;
      }
      
      .confidoo-brand-title {
        font-size: 2rem;
      }
    }
    
    .pulse-animation {
      animation: pulse 2s infinite;
    }
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
  `;

    return (
        <>
            <style>{customStyles}</style>
            <Row style={{ minHeight: '100vh' }} className="confidoo-login-container">
                <Col
                    className="formm"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px 20px',
                    }}
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 14 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                >
                    <div
                        className="confidoo-form-container"
                        style={{
                            width: '100%',
                            maxWidth: '420px',
                            padding: '48px 40px',
                        }}
                    >
                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <h1 className="confidoo-welcome-text" style={{ fontSize: '2rem', margin: 0 }}>
                                Welcome Back
                            </h1>
                            <p className="confidoo-subtitle" style={{ fontSize: '16px', margin: '8px 0 0 0' }}>
                                Sign in to your Confidoo account
                            </p>
                        </div>

                        <Form
                            form={form}
                            name="confidoo_login"
                            initialValues={{ remember: true }}
                            onFinish={handleSubmit}
                            layout="vertical"
                            size="large"
                        >
                            <Form.Item
                                name="email"
                                rules={[
                                    { required: true, message: 'Please enter your email address' },
                                    { type: 'email', message: 'Please enter a valid email address' },
                                ]}
                                style={{ marginBottom: '20px' }}
                            >
                                <Input
                                    prefix={<UserOutlined style={{ color: '#64748b' }} />}
                                    placeholder="Enter your email"
                                    className="confidoo-input"
                                    style={{ height: '48px', fontSize: '16px' }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[
                                    { required: true, message: 'Please enter your password' },
                                    { min: 8, message: 'Password must be at least 8 characters' },
                                ]}
                                style={{ marginBottom: '24px' }}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{ color: '#64748b' }} />}
                                    placeholder="Enter your password"
                                    className="confidoo-input"
                                    style={{ height: '48px', fontSize: '16px' }}
                                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '32px',
                                }}
                            >
                                <Form.Item name="remember" valuePropName="checked" style={{ margin: 0 }}>
                                    <Checkbox className="confidoo-checkbox">Remember me</Checkbox>
                                </Form.Item>

                                <a
                                    href="/auth-pass-reset"
                                    className="confidoo-forgot-link"
                                    style={{ textDecoration: 'none' }}
                                >
                                    Forgot password?
                                </a>
                            </div>

                            <Form.Item style={{ marginBottom: '24px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    block
                                    className="confidoo-button"
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </Form.Item>
                        </Form>

                        <div
                            style={{
                                textAlign: 'center',
                                paddingTop: '24px',
                                borderTop: '1px solid #e2e8f0',
                                color: '#64748b',
                                fontSize: '14px',
                            }}
                        >
                            Need help? Contact your system administrator
                        </div>
                    </div>
                </Col>

                <Col
                    xs={{ span: 0 }}
                    sm={{ span: 12 }}
                    md={{ span: 10 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    className="confidoo-logo-section"
                    style={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}
                >
                    <div
                        className="confidoo-lottie-container"
                        style={{
                            width: '100%',
                            maxWidth: '600px',
                            height: '400px',
                            marginBottom: '40px',
                        }}
                    >
                        <div
                            ref={lottieRef}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        />
                    </div>
                </Col>
            </Row>
            <Modal
                title="Two-factor Authentication"
                open={is2FAModalVisible}
                onCancel={handleModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleModalCancel}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" onClick={handle2FASubmit} loading={loading}>
                        Verify
                    </Button>,
                ]}
            >
                <p>Enter the 6-digit code from your authenticator app:</p>
                <Input
                    placeholder="6-digit code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    maxLength={6}
                    style={{ marginBottom: '20px' }}
                />
            </Modal>
        </>
    );
}

export default ConfidooLogin;